
import { Component, Vue } from "vue-property-decorator";
import { uiStore, configStore, documentStore } from "@/store/store";
import OnCanvasDragHandle from "./OnCanvasDragHandle.vue";
import { getNodeParent } from "@/lib/tree/tree";
import {
  getNodeElementWrapperStyles,
  getDomElementFromTreeNode,
  getNodeTitlebarStyles
} from "@/lib/canvas/helpers";
import { forceNextTick } from "@/util/force-next-tick";
import { getRightClickMenuPositionInCanvas } from "@/lib/right-click/actions";
import Icon from "@/components/UI/Icon.vue";
import { SECTION_SLUG } from "@/lib/elements/constants";
import lodash from "lodash";
import { keyboardShortcuts } from "@/lib/keyboard-shortcuts/keyboard-shortcuts";
import { KEYBOARD_SHORTCUTS } from "@/lib/keyboard-shortcuts/global-shortcuts";

@Component({
  components: {
    Icon,
    OnCanvasDragHandle
  }
})
export default class NodeTitlebar extends Vue {
  interval!: NodeJS.Timer;
  refreshRate = 500;

  unsubscribeFromStorePositionWrapper = () => {};
  nextTickPositionsWrapper() {
    forceNextTick(this.calculateStylesAndTitle);
  }

  openContextMenu(e: MouseEvent) {
    // don't reach the event in the menu, otherwise It'll select the wrong element
    e.stopPropagation();
    e.preventDefault();

    const position = getRightClickMenuPositionInCanvas(e.clientX, e.clientY);

    if (!uiStore.activeElementId || !position) return;

    uiStore.setOnCanvasRightClickMenuState({
      elementId: uiStore.activeElementId,
      position
    });
  }

  mounted() {
    this.calculateStylesAndTitle();

    this.unsubscribeFromStorePositionWrapper = this.$store.subscribe(
      this.nextTickPositionsWrapper
    );

    window.addEventListener("resize", this.nextTickPositionsWrapper);

    this.interval = setInterval(
      this.nextTickPositionsWrapper,
      this.refreshRate
    );

    keyboardShortcuts.register(KEYBOARD_SHORTCUTS.SHIFT_ESC, () => {
      this.activateParent();
    });
  }

  beforeDestroy() {
    this.unsubscribeFromStorePositionWrapper();

    window.removeEventListener("resize", this.nextTickPositionsWrapper);

    clearInterval(this.interval);

    keyboardShortcuts.unbind(KEYBOARD_SHORTCUTS.SHIFT_ESC);
  }

  get isDraftSection() {
    if (!uiStore.activeElement) return false;

    if (uiStore.activeElement.data.type !== SECTION_SLUG) return false;

    const isDraft = lodash.get(
      uiStore.activeElement.data.properties,
      "settings.advanced.draft"
    );

    return !!isDraft;
  }

  get isDragAndDropEnabled() {
    return uiStore.hasFullAccess;
  }

  get isElementUpdating() {
    if (!uiStore.activeElementId) return false;
    return !!uiStore.elementsThatAreUpdating[uiStore.activeElementId];
  }

  titlebarPositionStyle: { [key: string]: string } = {};

  wrapperPositionStyle: { [key: string]: string } = {
    display: "none"
  };

  elementTitle = "";

  // Everything is calculated in a func to avoid UI flicker caused by values being calculated at different times
  calculateStylesAndTitle() {
    if (uiStore.elementWithContentEditable || !uiStore.activeElement) {
      this.wrapperPositionStyle = {
        display: "none"
      };

      return;
    }

    this.elementTitle = configStore.element(
      uiStore.activeElement.data.type
    ).name;

    this.wrapperPositionStyle = getNodeElementWrapperStyles(
      getDomElementFromTreeNode(uiStore.activeElement)
    );

    this.titlebarPositionStyle = getNodeTitlebarStyles(uiStore.activeElement);
  }

  get titlebarClasses() {
    return {
      "node-titlebar-is-dragging": this.isDragging,
      "node-titlebar-is-draft-section": this.isDraftSection
    };
  }

  get isDragging() {
    return uiStore.currentlyDraggingCanvasElementId === uiStore.activeElementId;
  }

  duplicate() {
    if (uiStore.activeElementId) {
      documentStore.duplicateElementAndActivateIt(uiStore.activeElementId);
    }
  }

  remove() {
    if (uiStore.activeElementId !== null) {
      documentStore.deleteElement({ nodeId: uiStore.activeElementId });
    }
  }

  refreshSsr() {
    uiStore.setRefreshSsrElementId(uiStore.activeElementId);
  }

  get hasSSr() {
    const activeElementBase =
      uiStore.activeElement &&
      configStore.element(uiStore.activeElement.data.type);

    if (!activeElementBase) {
      return "";
    }

    return activeElementBase.template.includes("%%SSR%%");
  }

  get hasParent() {
    return this.parentElement && this.parentElement.data.type !== "root";
  }

  get node() {
    // False should never happen
    return uiStore.activeElement || false;
  }

  activateParent() {
    if (this.parentElement) {
      uiStore.activateElement(this.parentElement.id);
    }
  }

  get parentElement() {
    if (!uiStore.activeElement) return false;

    const maybeParent = getNodeParent(
      documentStore.document.tree,
      uiStore.activeElement.id
    );

    return maybeParent ? maybeParent : false;
  }
}
